import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPhoneDisplay(digits) {
  if (!digits) return "";
  // Ukraine: +380 XX XXX XXXX (12 digits)
  if (digits.startsWith("380") && digits.length === 12) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  // Germany: +49 XXX XXXX XXXX or similar
  if (digits.startsWith("49")) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)} ${digits.slice(9)}`.trim();
  }
  // Generic: +<CC 2-3 digits> <groups of 3>
  const cc = digits.slice(0, 2);
  const rest = digits.slice(2).replace(/(\d{3})(?=\d)/g, "$1 ");
  return `+${cc} ${rest}`.trim();
}

export default function OrderModal({ open, onOpenChange, product, items, onSuccess }) {
  const t = useT();
  const { whatsapp_supreme, telegram_username } = useSettings();
  const waDigits = (whatsapp_supreme || "").replace(/\D/g, "");
  const waDisplay = formatPhoneDisplay(waDigits);
  const tgHandle = (telegram_username || "SupremeLaboratories").replace(/^@/, "");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    address: "",
    house_number: "",
    address_2: "",
    city: "",
    region: "",
    postal_code: "",
    country: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setForm({
      first_name: "", last_name: "", address: "", house_number: "",
      address_2: "", city: "", region: "", postal_code: "", country: "",
      phone: "", email: "", notes: "",
    });
    setSuccess(false);
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!submitting) {
      onOpenChange(next);
      if (!next) setTimeout(reset, 300);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.address || !form.house_number || !form.postal_code || !form.city || !form.country || !form.phone || !form.email) {
      toast.error(t("co.err_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_id: product?.id,
        product_name: product?.name,
        product_category: product?.category,
        product_price: product?.price,
      };
      if (items && items.length) {
        payload.items = items.map((i) => ({
          product_id: i.id,
          product_name: i.name,
          product_category: i.category,
          product_price: i.price,
          quantity: i.quantity,
        }));
      }
      await axios.post(`${API}/orders`, payload);
      setSuccess(true);
      toast.success("Order submitted successfully");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalItemQty = items && items.length ? items.reduce((s, i) => s + i.quantity, 0) : 0;
  const headerTitle = items && items.length
    ? t(totalItemQty === 1 ? "co.title_checkout" : "co.title_checkout_plural", { n: totalItemQty })
    : (product?.name || t("co.title_new"));
  const orderTotal = items && items.length
    ? items.reduce((s, i) => s + i.price * i.quantity, 0)
    : product?.price;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="order-modal"
        className="bg-[#0A0A0A] border border-white/10 text-white w-[calc(100%-1.5rem)] max-w-lg p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        {!success ? (
          <>
            <div className="relative bg-gradient-to-b from-cyan-500/5 to-transparent border-b border-white/5 px-6 py-5 shrink-0">
              <DialogHeader>
                <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 mb-2 font-semibold">
                  Place Order
                </p>
                <DialogTitle className="font-headline text-2xl font-bold text-white">
                  {headerTitle}
                </DialogTitle>
                <DialogDescription className="text-zinc-500 text-sm">
                  Fill in your details below. Our team will contact you shortly to confirm.
                </DialogDescription>
                {items && items.length > 0 && (
                  <div className="mt-3 bg-[#050505] border border-white/5 rounded-md p-3 max-h-44 overflow-auto" data-testid="checkout-items-list">
                    {items.map((i) => (
                      <div key={i.id} className="flex justify-between text-xs py-1">
                        <span className="text-zinc-300 truncate pr-2">{i.name} <span className="text-zinc-600">×{i.quantity}</span></span>
                        <span className="text-[#39FF14] font-bold tabular-nums">€{(i.price * i.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 mt-2 border-t border-white/10 text-xs">
                      <span className="text-zinc-400 uppercase tracking-wider font-bold">{t("co.items_total")}</span>
                      <span className="text-[#39FF14] font-black tabular-nums text-sm">€{orderTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </DialogHeader>
            </div>

            <form onSubmit={submit} className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.first_name")}</Label>
                  <Input
                    id="first_name"
                    data-testid="order-firstname-input"
                    value={form.first_name}
                    onChange={update("first_name")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_name" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.last_name")}</Label>
                  <Input
                    id="last_name"
                    data-testid="order-lastname-input"
                    value={form.last_name}
                    onChange={update("last_name")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-[1fr_100px] gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="address" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.street")}</Label>
                  <Input
                    id="address"
                    data-testid="order-address-input"
                    value={form.address}
                    onChange={update("address")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="house_number" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.house_number")}</Label>
                  <Input
                    id="house_number"
                    data-testid="order-house-input"
                    value={form.house_number}
                    onChange={update("house_number")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="address_2" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.address_2")}</Label>
                <Input
                  id="address_2"
                  data-testid="order-address2-input"
                  value={form.address_2}
                  onChange={update("address_2")}
                  className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="postal_code" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.postal_code")}</Label>
                  <Input
                    id="postal_code"
                    data-testid="order-postal-input"
                    value={form.postal_code}
                    onChange={update("postal_code")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="city" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.city")}</Label>
                  <Input
                    id="city"
                    data-testid="order-city-input"
                    value={form.city}
                    onChange={update("city")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="region" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.region")}</Label>
                  <Input
                    id="region"
                    data-testid="order-region-input"
                    value={form.region}
                    onChange={update("region")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.country")}</Label>
                  <Input
                    id="country"
                    data-testid="order-country-input"
                    value={form.country}
                    onChange={update("country")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.phone")}</Label>
                  <Input
                    id="phone"
                    data-testid="order-phone-input"
                    value={form.phone}
                    onChange={update("phone")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="order-email-input"
                    value={form.email}
                    onChange={update("email")}
                    className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-zinc-400">{t("co.notes")}</Label>
                <Textarea
                  id="notes"
                  data-testid="order-notes-input"
                  value={form.notes}
                  onChange={update("notes")}
                  rows={2}
                  className="bg-[#050505] border-white/10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                />
              </div>

              <div className="flex items-start gap-2 bg-cyan-500/5 border border-cyan-500/15 rounded-lg px-3 py-2.5">
                <ShieldCheck className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  {t("co.secure_note")}
                </p>
              </div>

              <Button
                type="submit"
                data-testid="order-submit-btn"
                disabled={submitting}
                className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("co.submitting")}
                  </>
                ) : (
                  t("co.submit")
                )}
              </Button>
            </form>
          </>
        ) : (
          <div
            data-testid="order-success"
            className="px-6 py-10 text-center flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/40 flex items-center justify-center mb-5 shadow-[0_0_30px_rgba(57,255,20,0.3)]">
              <CheckCircle2 className="w-8 h-8 text-[#39FF14]" />
            </div>
            <h3 className="font-headline text-2xl font-bold mb-3 text-white">{t("co.success_title")}</h3>
            <p className="text-zinc-300 text-sm max-w-sm leading-relaxed mb-6">
              To complete your order, contact us on WhatsApp or Telegram:
            </p>

            <div className="w-full max-w-sm space-y-3">
              <a
                href={`https://wa.me/${waDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="order-success-whatsapp"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/40 text-[#25D366] font-bold transition group"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span className="text-sm tracking-wide">{waDisplay}</span>
              </a>

              <a
                href={`https://t.me/${tgHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="order-success-telegram"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-lg bg-[#229ED9]/10 hover:bg-[#229ED9]/20 border border-[#229ED9]/40 text-[#5EB8E4] font-bold transition group"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.833.941z" />
                </svg>
                <span className="text-sm tracking-wide">@{tgHandle}</span>
              </a>
            </div>

            <Button
              onClick={() => handleClose(false)}
              data-testid="order-success-close"
              className="mt-8 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold"
            >
              {t("co.close")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
